import React, { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { generateIntro } from '../lib/claude'
import { format, addDays, startOfDay } from 'date-fns'
import { buildEmailHtml } from '../lib/emailBuilder'
import { buildDigestText, hasDraft } from '../lib/digest'
import IssueEditor from './IssueEditor'
import ImageManager from './ImageManager'
import Subscribers from './Subscribers'
import Instructions from './Instructions'
import StoryFinder from '../components/StoryFinder'
import PublishModal from '../components/PublishModal'
import PreviewModal from '../components/PreviewModal'
import IntroModal from '../components/IntroModal'
import DeleteModal from '../components/DeleteModal'
import {
  DroppableColumn, SortableCard, DragOverlayCard, IntroCard, storyAccent
} from '../components/KanbanCards'
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  closestCorners, defaultDropAnimationSideEffects
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.4' } } })
}

const QUEUE = 'queue'
const NO_STORIES = []
const byPosition = (a, b) => a.position - b.position
const DAY_KEY = 'yyyy-MM-dd'

export default function Calendar() {
  const [issues, setIssues] = useState([])
  const [stories, setStories] = useState([])
  const [intros, setIntros] = useState({}) // issueId -> {text, subject, loading}
  const [loading, setLoading] = useState(true)
  const [deleteModal, setDeleteModal] = useState(null)
  const [activeDrag, setActiveDrag] = useState(null)
  const [dragOrigin, setDragOrigin] = useState(null) // {issueId, position} before drag
  const [showFinder, setShowFinder] = useState(false)
  const [showImageManager, setShowImageManager] = useState(false)
  const [showSubscribers, setShowSubscribers] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [editingStory, setEditingStory] = useState(null)
  const [previewIssue, setPreviewIssue] = useState(null)
  const [introModal, setIntroModal] = useState(null)
  const [publishModal, setPublishModal] = useState(null)
  const gridRef = useRef(null)

  const today = startOfDay(new Date())
  const todayKey = format(today, DAY_KEY)
  const pastDays = Array.from({ length: 30 }, (_, i) => addDays(today, -(30 - i)))
  const futureDays = Array.from({ length: 14 }, (_, i) => addDays(today, i))

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  // Stories grouped by column (issue id, or QUEUE) and sorted once per change
  const columns = useMemo(() => {
    const map = new Map()
    for (const s of stories) {
      const key = s.issue_id || QUEUE
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(s)
    }
    map.forEach(list => list.sort(byPosition))
    return map
  }, [stories])

  const issuesByDate = useMemo(() => new Map(issues.map(i => [i.send_date, i])), [issues])

  const getIssueForDate = date => issuesByDate.get(format(date, DAY_KEY))
  const getStoriesForIssue = issueId => columns.get(issueId) || NO_STORIES
  const queueStories = columns.get(QUEUE) || NO_STORIES

  useEffect(() => { fetchData() }, []) // eslint-disable-line

  useEffect(() => {
    if (!loading && gridRef.current) {
      const queueEl = gridRef.current.querySelector('.kanban-col--queue')
      if (queueEl) {
        gridRef.current.scrollLeft = queueEl.offsetLeft - gridRef.current.offsetLeft
      }
    }
  }, [loading])

  async function fetchData() {
    setLoading(true)
    const since = format(addDays(today, -30), DAY_KEY)
    const { data: issueData } = await supabase.from('issues').select('*')
      .gte('send_date', since).order('send_date')
    const issueList = issueData || []

    // Only load stories the board can show: the queue plus the issues in view
    let storyQuery = supabase.from('stories').select('*').order('position')
    storyQuery = issueList.length
      ? storyQuery.or(`issue_id.is.null,issue_id.in.(${issueList.map(i => i.id).join(',')})`)
      : storyQuery.is('issue_id', null)
    const { data: storyData } = await storyQuery

    setIssues(issueList)
    setStories(storyData || [])

    // Restore intros from saved subject_line field
    const introMap = {}
    for (const issue of issueList) {
      if (issue.subject_line) {
        introMap[issue.id] = { text: issue.intro_text || '', subject: issue.subject_line, loading: false }
      }
    }
    setIntros(introMap)
    setLoading(false)
  }

  async function ensureIssue(day) {
    const dateStr = format(day, DAY_KEY)
    const existing = issuesByDate.get(dateStr)
    if (existing) return existing
    const { data } = await supabase.from('issues')
      .insert({ send_date: dateStr, status: 'draft' }).select().single()
    if (data) setIssues(prev => [...prev, data])
    return data
  }

  // ── Intro ───────────────────────────────────────────────
  async function refreshIntro(issue) {
    const colStories = getStoriesForIssue(issue.id)
    if (colStories.length === 0) return
    setIntros(prev => ({ ...prev, [issue.id]: { ...prev[issue.id], loading: true } }))
    const result = await generateIntro(colStories, issue.send_date)
    const introText = typeof result === 'string' ? result : result.intro
    const subjectText = typeof result === 'string'
      ? (colStories[0]?.headline?.toLowerCase() || '') : result.subject
    setIntros(prev => ({ ...prev, [issue.id]: { text: introText, subject: subjectText, loading: false } }))
    await supabase.from('issues').update({ subject_line: subjectText, intro_text: introText }).eq('id', issue.id)
  }

  function updateIntroField(issueId, field, value) {
    setIntros(prev => ({ ...prev, [issueId]: { ...prev[issueId], [field]: value } }))
  }

  async function saveIntroFields(issueId) {
    const intro = intros[issueId]
    if (!intro) return
    await supabase.from('issues').update({
      subject_line: intro.subject,
      intro_text: intro.text
    }).eq('id', issueId)
  }

  // Wipe an issue's intro and subject once its last story has left the column
  async function clearIntroIfEmpty(issueId, leavingStoryId) {
    if (!issueId) return
    const stillHasStories = stories.some(s => s.issue_id === issueId && s.id !== leavingStoryId)
    if (stillHasStories) return
    setIntros(prev => ({ ...prev, [issueId]: { text: '', subject: '', loading: false } }))
    await supabase.from('issues').update({ subject_line: '', intro_text: '' }).eq('id', issueId)
  }

  // ── Add stories ─────────────────────────────────────────
  async function addStoryToQueue(proposal, angle, isRubric) {
    const isNotableAscent = proposal.story_type === 'notable_ascent'
    // For notable ascents, the angle description IS the pre-written bullet text
    const draftText = isNotableAscent ? angle.description : null
    const { data } = await supabase.from('stories').insert({
      issue_id: null, position: queueStories.length,
      status: isNotableAscent ? 'drafted' : 'selected',
      sport_slug: proposal.sport_slug || 'FEATURE',
      headline: proposal.headline,
      angle: angle.label + ': ' + angle.description,
      proposed_angles: proposal.angles || [],
      search_queries: [],
      is_rubric: isRubric || false,
      is_manual: false,
      story_type: proposal.story_type || 'regular',
      draft_text: draftText,
      edited_text: draftText
    }).select().single()
    if (data) setStories(prev => [...prev, data])
  }

  async function addManualCard(targetIssueId, day) {
    // If no issue yet for this day, create one
    let issue = targetIssueId ? issues.find(i => i.id === targetIssueId) : null
    if (!issue && day) issue = await ensureIssue(day)

    const colStories = issue ? getStoriesForIssue(issue.id) : queueStories

    const { data } = await supabase.from('stories').insert({
      issue_id: issue ? issue.id : null,
      position: colStories.length,
      status: 'selected',
      sport_slug: 'NEW STORY',
      headline: 'Untitled story',
      angle: '',
      proposed_angles: [],
      search_queries: [],
      is_manual: true
    }).select().single()

    if (data) {
      setStories(prev => [...prev, data])
      setEditingStory(data)
    }
  }

  // ── Drag ────────────────────────────────────────────────
  function handleDragStart({ active }) {
    const story = stories.find(s => s.id === active.id)
    setActiveDrag(story || null)
    setDragOrigin(story ? { issueId: story.issue_id || null, position: story.position } : null)
  }

  function handleDragOver({ active, over }) {
    if (!over || active.id === over.id) return
    const activeStory = stories.find(s => s.id === active.id)
    if (!activeStory) return

    const overStory = stories.find(s => s.id === over.id)
    const overIssue = issues.find(i => i.id === over.id)
    let targetIssueId

    if (over.id === QUEUE) targetIssueId = null
    else if (overStory) targetIssueId = overStory.issue_id || null
    else if (overIssue) targetIssueId = overIssue.id
    else return

    const sourceIssueId = activeStory.issue_id || null

    if (sourceIssueId === targetIssueId) {
      // Same column reorder
      const colStories = columns.get(sourceIssueId || QUEUE) || NO_STORIES
      const oldIndex = colStories.findIndex(s => s.id === active.id)
      const newIndex = overStory ? colStories.findIndex(s => s.id === over.id) : colStories.length - 1
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      const reordered = arrayMove(colStories, oldIndex, newIndex)
      setStories(prev => {
        const others = prev.filter(s => (s.issue_id || null) !== sourceIssueId)
        return [...others, ...reordered.map((s, i) => ({ ...s, position: i }))]
      })
    } else {
      // Cross-column move
      const targetStories = (columns.get(targetIssueId || QUEUE) || NO_STORIES).filter(s => s.id !== active.id)
      let insertIndex = targetStories.length
      if (overStory && (overStory.issue_id || null) === targetIssueId) {
        const idx = targetStories.findIndex(s => s.id === over.id)
        if (idx >= 0) insertIndex = idx
      }
      setStories(prev => {
        const withoutActive = prev.filter(s => s.id !== active.id)
        const newTarget = withoutActive
          .filter(s => (s.issue_id || null) === targetIssueId)
          .sort(byPosition)
        newTarget.splice(insertIndex, 0, { ...activeStory, issue_id: targetIssueId })
        const others = withoutActive.filter(s => (s.issue_id || null) !== targetIssueId)
        return [...others, ...newTarget.map((s, i) => ({ ...s, position: i }))]
      })
    }
  }

  async function handleDragEnd({ active, over }) {
    setActiveDrag(null)
    const origin = dragOrigin
    setDragOrigin(null)

    if (!over) {
      // Dropped outside — restore to origin
      if (origin) {
        setStories(prev => prev.map(s =>
          s.id === active.id ? { ...s, issue_id: origin.issueId, position: origin.position } : s
        ))
      }
      return
    }

    const story = stories.find(s => s.id === active.id)
    if (!story) return

    let finalIssueId = story.issue_id || null
    let finalPosition = story.position

    // Dropping onto an empty date column that has no issue yet: create the issue
    const droppedOnKnownTarget = over.id === QUEUE
      || stories.some(s => s.id === over.id) || issues.some(i => i.id === over.id)
    if (!droppedOnKnownTarget) {
      const day = futureDays.find(d => format(d, DAY_KEY) === over.id)
      if (day) {
        const issue = await ensureIssue(day)
        if (issue) {
          finalIssueId = issue.id
          finalPosition = 0
          setStories(prev => prev.map(s =>
            s.id === active.id ? { ...s, issue_id: issue.id, position: 0 } : s
          ))
        }
      }
    }

    await supabase.from('stories')
      .update({ issue_id: finalIssueId, position: finalPosition, updated_at: new Date().toISOString() })
      .eq('id', active.id)

    const siblings = stories.filter(s => (s.issue_id || null) === finalIssueId && s.id !== active.id)
    await Promise.all(siblings.map(s =>
      supabase.from('stories').update({ position: s.position }).eq('id', s.id)
    ))

    if (origin?.issueId && origin.issueId !== finalIssueId) {
      await clearIntroIfEmpty(origin.issueId, active.id)
    }
  }

  // ── Remove ──────────────────────────────────────────────
  async function handleMoveToQueue(story) {
    await supabase.from('stories')
      .update({ issue_id: null, status: 'saved', updated_at: new Date().toISOString() }).eq('id', story.id)
    setStories(prev => prev.map(s => s.id === story.id ? { ...s, issue_id: null, status: 'saved' } : s))
    await clearIntroIfEmpty(story.issue_id, story.id)
    setDeleteModal(null)
  }

  async function handleDelete(story) {
    await supabase.from('stories').delete().eq('id', story.id)
    setStories(prev => prev.filter(s => s.id !== story.id))
    await clearIntroIfEmpty(story.issue_id, story.id)
    setDeleteModal(null)
  }

  // ── Publish ─────────────────────────────────────────────
  function openPublishModal(issue) {
    const colStories = getStoriesForIssue(issue.id)
    if (!colStories.some(hasDraft)) { alert('No drafted stories to publish.'); return }
    const digest = buildDigestText({
      sendDate: issue.send_date,
      intro: intros[issue.id]?.text || '',
      stories: colStories
    })
    setPublishModal({ issue, subject: intros[issue.id]?.subject || 'Inside Mag', html: buildEmailHtml(digest) })
  }

  // ── Update story in local state ─────────────────────────
  function updateStoryInState(updatedStory) {
    setStories(prev => prev.map(s => s.id === updatedStory.id ? { ...s, ...updatedStory } : s))
  }

  if (loading) return <div className="loading">Loading…</div>

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners}
      onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>

      <div className="calendar">
        <div className="calendar-header">
          <h1 className="calendar-title">Editorial Board</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-secondary" onClick={() => setShowInstructions(true)}>Instructions</button>
            <button className="btn-secondary" onClick={() => setShowSubscribers(true)}>Subscribers</button>
            <button className="btn-secondary" onClick={() => setShowImageManager(true)}>Images</button>
          </div>
        </div>

        <div className="calendar-grid" ref={gridRef}>
          {/* Past columns */}
          {pastDays.map(day => {
            const issue = getIssueForDate(day)
            const dateStr = format(day, DAY_KEY)
            if (!issue) {
              return (
                <div key={dateStr} className="kanban-col kanban-col--past">
                  <div className="kanban-col-header">
                    <div className="col-date-info">
                      <span className="col-day">{format(day, 'EEE')}</span>
                      <span className="col-num">{format(day, 'd')}</span>
                    </div>
                  </div>
                </div>
              )
            }
            return (
              <div key={dateStr} className="kanban-col kanban-col--past">
                <div className="kanban-col-header">
                  <div className="col-date-info">
                    <span className="col-day">{format(day, 'EEE')}</span>
                    <span className="col-num">{format(day, 'd')}</span>
                    <span className="col-month">{format(day, 'MMM')}</span>
                  </div>
                  <span className="past-status">{issue.status}</span>
                </div>
                <div className="kanban-cards">
                  {getStoriesForIssue(issue.id).map(story => (
                    <div key={story.id}
                      className="story-card-k"
                      style={{ borderLeftColor: storyAccent(story), cursor: 'pointer' }}
                      onClick={() => setEditingStory(story)}>
                      <span className="card-k-slug" style={{ color: storyAccent(story) }}>{story.sport_slug}</span>
                      <p className="card-k-headline">{story.headline}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          {/* Queue */}
          <DroppableColumn id={QUEUE} className="kanban-col kanban-col--queue">
            <div className="kanban-col-header">
              <span className="kanban-col-title">QUEUE <span className="kanban-col-count">{queueStories.length}</span></span>
            </div>
            <div className="queue-actions">
              <button className="queue-action-btn queue-action-btn--rust" onClick={() => setShowFinder(true)}>FIND</button>
              <button className="queue-action-btn queue-action-btn--dark" onClick={() => addManualCard(null, null)}>ADD</button>
            </div>
            <SortableContext items={queueStories.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="kanban-cards">
                {queueStories.map(story => (
                  <SortableCard key={story.id} story={story} inQueue={true}
                    onDelete={() => setDeleteModal({ story, inQueue: true })}
                    onClick={() => setEditingStory(story)} />
                ))}
                {queueStories.length === 0 && <div className="kanban-empty">No stories in queue</div>}
              </div>
            </SortableContext>
            <div className="kanban-col-footer" />
          </DroppableColumn>

          {/* Future date columns */}
          {futureDays.map(day => {
            const issue = getIssueForDate(day)
            const colStories = issue ? getStoriesForIssue(issue.id) : NO_STORIES
            const dateStr = format(day, DAY_KEY)
            const isToday = dateStr === todayKey
            const colId = issue ? issue.id : dateStr
            const intro = issue ? intros[issue.id] : null
            const hasStories = colStories.length > 0
            const isScheduled = issue?.status === 'scheduled'

            return (
              <DroppableColumn key={dateStr} id={colId}
                className={'kanban-col' + (isToday ? ' kanban-col--today' : '') + (isScheduled ? ' kanban-col--scheduled' : '')}>
                <div className="kanban-col-header">
                  <div className="col-date-info">
                    <span className="col-day">{format(day, 'EEE')}</span>
                    <span className="col-num">{format(day, 'd')}</span>
                    {isToday && !isScheduled && issue?.status !== 'ready' && <span className="today-badge">TODAY</span>}
                    {isScheduled && <span className="scheduled-badge">SCHEDULED</span>}
                    {issue?.status === 'ready' && <span className="sent-badge">SENT</span>}
                  </div>
                </div>

                <SortableContext items={colStories.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className="kanban-cards">
                    {hasStories && issue && (
                      <IntroCard intro={intro} onRefresh={() => refreshIntro(issue)}
                        onClick={() => setIntroModal({ issue, intro })} />
                    )}
                    {colStories.map(story => (
                      <SortableCard key={story.id} story={story} inQueue={false}
                        onDelete={() => setDeleteModal({ story, inQueue: false })}
                        onClick={() => setEditingStory(story)} />
                    ))}
                    {!hasStories && <div className="kanban-empty">Drop stories here</div>}
                  </div>
                </SortableContext>

                <div className="kanban-col-footer">
                  <button className="kanban-add-btn" onClick={() => addManualCard(issue ? issue.id : null, day)}>+</button>
                  {issue && hasStories && (
                    <>
                      <button className="kanban-preview-btn" onClick={() => setPreviewIssue(issue)} title="Preview email">👁</button>
                      <button className="kanban-publish-btn" onClick={() => openPublishModal(issue)}>
                        PUBLISH
                      </button>
                    </>
                  )}
                </div>
              </DroppableColumn>
            )
          })}
        </div>

        {showFinder && <StoryFinder onClose={() => setShowFinder(false)} onAddStory={addStoryToQueue} />}
        {showImageManager && <ImageManager onClose={() => setShowImageManager(false)} />}
        {showSubscribers && <Subscribers onClose={() => setShowSubscribers(false)} />}
        {showInstructions && <Instructions onClose={() => setShowInstructions(false)} />}
        {publishModal && (
          <PublishModal
            issue={publishModal.issue}
            subject={publishModal.subject}
            html={publishModal.html}
            onClose={() => setPublishModal(null)}
            onSent={() => {
              fetchData()
              setPublishModal(null)
            }}
          />
        )}
        {editingStory && <IssueEditor story={editingStory}
          onClose={(updatedStory) => {
            if (updatedStory) updateStoryInState(updatedStory)
            setEditingStory(null)
          }} />}
        {introModal && <IntroModal intro={introModal.intro}
          onRefresh={() => refreshIntro(introModal.issue)}
          onChange={(field, value) => updateIntroField(introModal.issue.id, field, value)}
          onClose={() => { saveIntroFields(introModal.issue.id); setIntroModal(null) }} />}
        {previewIssue && <PreviewModal issue={previewIssue}
          stories={getStoriesForIssue(previewIssue.id)}
          intro={intros[previewIssue.id]?.text || ''}
          onClose={() => setPreviewIssue(null)} />}
        {deleteModal && <DeleteModal story={deleteModal.story} inQueue={deleteModal.inQueue}
          onCancel={() => setDeleteModal(null)}
          onDelete={() => handleDelete(deleteModal.story)}
          onQueue={() => handleMoveToQueue(deleteModal.story)} />}
      </div>

      <DragOverlay dropAnimation={dropAnimation}>
        {activeDrag ? <DragOverlayCard story={activeDrag} /> : null}
      </DragOverlay>
    </DndContext>
  )
}
